import React, { useState } from 'react';
import { TabKeyIdentifierPrefix } from './Tab';
import './../../scss/controls/_tabpanel.scss';

export enum TabPanelTheme {
    Blue = 'blue',
    Gray = 'gray',
    White = 'white',
    Red = 'red',
    Whiteout = 'whiteout',
}

export enum TabButtonStyle {
    Fill = 'tabButtonStyle-fill',
    Condensed = 'tabButtonStyle-condensed',
}

export enum TabPanelBorderRadius {
    None = 'noRadius',
    Small = 'smallRadius',
    Medium = 'mediumRadius',
}

export const TabPanel: React.FC<{
    id: string;
    theme: TabPanelTheme;
    defaultIndex?: number;
    activeIndex?: number;
    onTabChange?: (index: number) => void;
    hint?: string;
    width?: string;
    height?: string;
    children: any;
    tabButtonStyle?: TabButtonStyle;
    tabPanelBorderRadius?: TabPanelBorderRadius;
}> = ({
    id,
    defaultIndex = 0,
    activeIndex,
    onTabChange,
    theme,
    hint,
    width = 'auto',
    height = 'auto',
    children,
    tabButtonStyle = TabButtonStyle.Fill,
    tabPanelBorderRadius = TabPanelBorderRadius.None,
}) => {
    const [internalIndex, setInternalIndex] = useState(defaultIndex);

    // Use controlled index if provided, otherwise use internal state
    const currentIndex = activeIndex !== undefined ? activeIndex : internalIndex;

    const handleTabClick = (index: number) => {
        if (onTabChange) {
            onTabChange(index); // Notify parent
        } else {
            setInternalIndex(index); // Manage internally
        }
    };

    const tabs = React.Children.map(children, (child) => {
        return child.key?.startsWith(TabKeyIdentifierPrefix) ? child : null;
    }).filter(Boolean);

    const setStyle = {
        width: width,
        height: height,
    };

    const inactiveStyle = (activeIndex: number, currentIndex: number) => {
        if (currentIndex === activeIndex - 1) {
            return 'inactiveLeft';
        } else if (currentIndex === activeIndex + 1) {
            return 'inactiveRight';
        } else {
            return 'inactive';
        }
    };

    return (
        <>
            <div
                id={id}
                className={`tabPanel ${theme} ${tabButtonStyle}`}
                style={setStyle}
                title={hint}
            >
                <div
                    className={`tabList ${theme}`}
                    role='tablist'
                >
                    {tabs &&
                        tabs.map((tab, index) => (
                            <button
                                key={index}
                                className={`tabButton ${theme} ${currentIndex === index ? 'active' : inactiveStyle(currentIndex, index)} ${tabButtonStyle}`}
                                onClick={() => handleTabClick(index)}
                                role='tab'
                                aria-selected={currentIndex === index}
                                aria-controls={`tab-panel-${index}`}
                                id={`tab-${index}`}
                            >
                                {tab.props.text}
                            </button>
                        ))}
                </div>
                <div className={`tabContent ${theme} ${tabPanelBorderRadius}`}>
                    {tabs && tabs[currentIndex] && (
                        <div
                            id={`tab-panel-${activeIndex}`}
                            aria-labelledby={`tab-${activeIndex}`}
                        >
                            {tabs[currentIndex]}
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};
